import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { ColDef } from "ag-grid-enterprise";
import {
  AddInstructions,
  AddNoteWithFile,
} from "../../api/preworkDetailApi/preworkDetailApi";
import { urlConfig } from "../../config/urlConfig";
import { useSnackbar } from "../Common/SnackbarContext";
import { useLoader } from "../../context/LoaderContext";
import LocalStorageUtil from "../../utils/localStorage";

interface AddNotesProps {
  isOpen: boolean;
  onClose: (isCancelClick: boolean, tabValue?: number) => void;
  title: string;
  selectedData: any;
  screenType: string | null | undefined;
  tabValue?: number;
}
const AddNotes: React.FC<AddNotesProps> = ({
  isOpen,
  onClose,
  title,
  selectedData,
  screenType,
  tabValue,
}) => {
  const [files, setFiles] = useState<FileList | null>(null);
  const { showSnackbar } = useSnackbar();
  const [rowData, setRowData] = useState<any[]>([]);
  const [noteText, setNoteText] = useState<string>("");
  const { setLoading } = useLoader();
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );

  const [selectFileValidation, setSelectFileValidation] = useState<
    string | null
  >(null);

  //Action Cell Renderer
  const actionCellRenderer = (params: any) => {
    if (params.data === undefined) {
      return "";
    } else {
      return (
        <button
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            color: "var(--aggrid-text-color)",
            display: "flex",
            alignItems: "center",
          }}
          onClick={() => handleDeleteRow(params.data.fileName)}
        >
          <CloseIcon />
        </button>
      );
    }
  };

  //Defined grid column
  const [columnDef] = useState<ColDef[]>([
    {
      headerName: "File Name",
      field: "fileName",
      sortable: true,
      filter: true,
      resizable: true,
      width: 150,
    },
    {
      headerName: "File Size",
      field: "fileSize",
      sortable: true,
      filter: true,
      resizable: true,
      width: 130,
    },
    {
      headerName: "Action",
      field: "action",
      width: 110,
      sortable: false,
      resizable: false,
      cellRenderer: actionCellRenderer,
    },
  ]);

  //Handle delete row
  const handleDeleteRow = (fileName: string) => {
    setRowData((prevRowData) =>
      prevRowData.filter((row) => row.fileName !== fileName)
    );
  };

  //Handle Add button click
  const handelAddClick = async () => {
    setLoading(true);
    try {
      if (screenType === "PreworkDetails") {
        if (noteText === undefined || noteText === "") {
          setValidationMessage("Enter Notes text to proceed");
          return;
        } else {
          //Api call
          const params = {
            InstrunctionCreatedBy: Number(LocalStorageUtil.getItem("userId")),
            Instructiontext: noteText,
            Addnotesemail: true,
            Accountid: Number(LocalStorageUtil.getItem("accountId")),
            ChildFileDocumentStreamID: selectedData.childFileDocumentStreamID,
            FinalDocumentid: selectedData.finalDocumentid,
            Filename: selectedData.Filename,
            contextName: LocalStorageUtil.getItem("contextName") ?? "",
            ParentDocumentID: selectedData.childDocumentID,
          };
          const formData = new FormData();
          const urldata = new URLSearchParams(
            Object.entries(params).reduce((acc, [key, value]) => {
              acc[key] = String(value);
              return acc;
            }, {} as Record<string, string>)
          ).toString();
          formData.append(
            "paramGetUrl",
            `${urlConfig.PreDetailsAddinstructions}?${urldata}`
          );
          if (files) {
            Array.from(files).forEach((file) => {
              formData.append(`files`, file);
            });
          }

          const response =
            rowData.length === 0
              ? await AddInstructions(params)
              : await AddNoteWithFile(formData);
          if (response) {
            showSnackbar("Notes added successfully");
            setValidationMessage(null);
            onClose(false, tabValue);
          } else {
            console.log("Error in adding notes");
          }
        }
      }
    } catch (error) {
      console.error("Error in handelAddClick", error);
    } finally {
      setLoading(false);
    }
  };

  //Handle select files using open file dialog box
  const handelSelectFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const excelFiles = Array.from(files).filter(
        (file) =>
          file.name.endsWith(".xls") ||
          file.name.endsWith(".xlsx") ||
          file.name.endsWith(".pdf") ||
          file.name.endsWith(".txt")
      );
      const newRowData = excelFiles.map((file) => ({
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      }));
      if (newRowData.length > 0) {
        setRowData((prevRowData) => [...prevRowData, ...newRowData]);
        setFiles(files);
        setSelectFileValidation(null);
      } else {
        setSelectFileValidation(
          "Please select valid file types(.xls,.xlsx,.pdf,.txt)"
        );
      }
    }
  };

  //Handle close button click
  const handleCloseClick = () => {
    onClose(true);
  };

  const handelNoteTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setNoteText(event.target.value);
    } catch (error) {
      console.error("Error in handelNoteTextChange", error);
      throw error;
    } finally {
      validationMessage !== null && setValidationMessage(null);
    }
  };

  return (
    <div>
      <Dialog
        open={isOpen}
        onClose={handleCloseClick}
        maxWidth="xs"
        PaperProps={{
          style: {
            height: "55%",
            width: "30%",
          },
        }}
      >
        <DialogTitle
          style={{
            backgroundColor: "var(--primary-color)",
            color: "var(--aggrid-text-color)",
            fontSize: "14px",
          }}
        >
          <div
            className="col-sm-12"
            style={{
              display: "flex",
              justifyContent: "left",
              alignItems: "center",
            }}
          >
            <div
              className="col-sm-11"
              style={{
                display: "flex",
                alignItems: "left",

                color: "var(--aggrid-text-color)",
              }}
            >
              {title}
            </div>
            <div
              className="col-sm-1"
              style={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "right",
              }}
            >
              {" "}
              <Tooltip title="Close">
                <IconButton
                  onClick={handleCloseClick}
                  style={{
                    cursor: "pointer",
                    alignItems: "flex-end",
                    color: "var(--aggrid-text-color)",
                    width: "20px",
                  }}
                >
                  <CloseIcon onClick={handleCloseClick} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "20px",
              height: "100px",
            }}
          >
            <TextField
              multiline
              minRows={10}
              aria-label="maxWidth"
              maxRows={4}
              value={noteText}
              onChange={handelNoteTextChange}
              style={{
                width: "100%",
                height: "100%",
                overflow: "auto",
              }}
              placeholder="Enter Notes text here"
              inputProps={{
                style: {
                  backgroundColor: "var(--aggrid-background-color)",
                  font: "menu",
                  fontFamily: "monospace",
                },
              }}
            />
          </div>
          {validationMessage && (
            <Typography color="error" style={{ marginTop: "10px" }}>
              {validationMessage}
            </Typography>
          )}
          <div>
            <input
              type="file"
              multiple
              onChange={handelSelectFiles}
              style={{
                display: "none",
              }}
              id="file-upload-input"
            />
            <label htmlFor="file-upload-input">
              <Button
                component="span"
                style={{
                  textTransform: "none",
                  borderRadius: "0px",
                  backgroundColor: "var(--button-background-color)",
                  color: "var(--button-color)",
                  fontSize: "11px",
                  padding: "5px 12px",
                  border: "1px solid #144979",
                  margin: "20px 5px 5px 50px",
                  whiteSpace: "white-space",
                  width: "160%",
                }}
              >
                Attach file to Notes
              </Button>
            </label>
          </div>
          {selectFileValidation && (
            <Typography color="error" style={{ marginTop: "10px" }}>
              {selectFileValidation}
            </Typography>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "20px",
            }}
          ></div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "5px",

              height: "100px",
            }}
          >
            <div
              className="ag-theme-quartz-dark"
              style={{
                height: "20vh",
                width: "99%",
                marginTop: "90px",
              }}
            >
              <AgGridReact
                rowData={rowData}
                columnDefs={columnDef}
                headerHeight={25}
                gridOptions={{
                  rowSelection: {
                    mode: "multiRow",
                    enableClickSelection: false,
                  },
                }}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handelAddClick}
            style={{
              fontSize: "14px",
              height: "2.1em",
              padding: "4px 6px",
              border: "1px solid #144979",
            }}
          >
            Add
          </Button>
          <Button
            style={{
              fontSize: "14px",
              height: "2.1em",
              padding: "4px 6px",
              border: "1px solid #144979",
            }}
            onClick={handleCloseClick}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AddNotes;
